import { useEffect, useRef, useState } from 'react';

import { ZONES, type ZoneId } from './zones';

const ZONE_ORDER: ZoneId[] = ['analytics', 'tasks'];

export default function ZoneDrum({
  activeZone,
  onSwitch,
}: {
  activeZone: ZoneId;
  onSwitch: (id: ZoneId) => void;
}) {
  const activeIndex = ZONE_ORDER.indexOf(activeZone);
  const [animating, setAnimating] = useState(false);
  const [displayIndex, setDisplayIndex] = useState(activeIndex);
  const [direction, setDirection] = useState<'up' | 'down'>('down');
  const prevIndex = useRef(activeIndex);

  useEffect(() => {
    if (activeIndex === prevIndex.current) return;
    const dir = activeIndex > prevIndex.current ? 'down' : 'up';
    setDirection(dir);
    setAnimating(true);
    const t = setTimeout(() => {
      setDisplayIndex(activeIndex);
      setAnimating(false);
      prevIndex.current = activeIndex;
    }, 180);
    return () => clearTimeout(t);
  }, [activeIndex]);

  const currentIdx = animating ? prevIndex.current : displayIndex;
  const prevZone = ZONES[currentIdx - 1] ?? null;
  const currentZone = ZONES[currentIdx];
  const nextZone = ZONES[currentIdx + 1] ?? null;

  const handleClick = () => {
    const nextIdx = (currentIdx + 1) % ZONES.length;
    onSwitch(ZONES[nextIdx].id);
  };

  const slotH = 20;

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown' && nextZone) {
          event.preventDefault();
          onSwitch(nextZone.id);
        }
        if (event.key === 'ArrowUp' && prevZone) {
          event.preventDefault();
          onSwitch(prevZone.id);
        }
      }}
      title="Переключить режим"
      aria-label={`Текущий раздел: ${currentZone?.label}. Переключить раздел`}
      className="group relative flex h-9 items-center gap-1.5 rounded-md pl-3 pr-2 select-none cursor-pointer transition-colors hover:bg-surface-100"
    >
      <span className="absolute left-0 top-1/2 h-5 w-px -translate-y-1/2 bg-surface-200 transition-colors" />

      <div
        className="zone-drum-window relative w-[92px] overflow-hidden"
        style={{ height: slotH * 2.6 }}
      >
        <div
          className="pointer-events-none absolute inset-x-1 z-[5] border-b border-surface-200 transition-colors"
          style={{ top: slotH * 0.8 }}
        />
        <div
          className="pointer-events-none absolute inset-x-1 z-[5] border-t border-surface-200 transition-colors"
          style={{ bottom: slotH * 0.8 }}
        />

        <div
          className={`flex flex-col transition-transform ease-out ${animating ? 'duration-200' : 'duration-0'}`}
          style={{
            transform: animating
              ? direction === 'down'
                ? `translateY(-${slotH}px)`
                : `translateY(${slotH}px)`
              : 'translateY(0)',
          }}
        >
          <div
            className="flex items-center justify-center shrink-0"
            style={{ height: slotH * 0.8 }}
          >
            {prevZone && (
              <span
                className="text-xs font-medium text-surface-400 transition-colors"
                style={{ lineHeight: 1 }}
              >
                {prevZone.label}
              </span>
            )}
          </div>

          <div className="flex items-center justify-center shrink-0" style={{ height: slotH }}>
            <span className="whitespace-nowrap text-sm font-semibold text-surface-900 transition-colors">
              {currentZone?.label}
            </span>
          </div>

          <div
            className="flex items-center justify-center shrink-0"
            style={{ height: slotH * 0.8 }}
          >
            {nextZone && (
              <span
                className="text-xs font-medium text-surface-400 transition-colors"
                style={{ lineHeight: 1 }}
              >
                {nextZone.label}
              </span>
            )}
          </div>
        </div>
      </div>

      <span className="flex flex-col items-center gap-0.5 text-surface-500 opacity-50 transition-opacity group-hover:opacity-100">
        <i className="ri-arrow-up-s-line text-xs leading-none" style={{ lineHeight: '10px' }} />
        <i className="ri-arrow-down-s-line text-xs leading-none" style={{ lineHeight: '10px' }} />
      </span>
    </button>
  );
}
